import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, X, CheckCircle2, Flag, Share2, Radio, PiggyBank, ScanLine } from "lucide-react";
import client, { money, apiErr, STORE_COLORS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FlowSteps from "@/components/FlowSteps";
import OpenInMaps from "@/components/OpenInMaps";
import ReceiptScanner from "@/components/ReceiptScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function Completed({ trip, navigate }) {
  const savedPos = (trip.saved ?? 0) >= 0;
  return (
    <div className="max-w-lg mx-auto space-y-5 pp-fade-up">
      <FlowSteps current={4} listId={trip.list_id} tripId={trip.id} />
      <div className="bg-white rounded-3xl p-8 pp-shadow-lg text-center">
        <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-[#EBF7EE] pp-pop"><CheckCircle2 className="h-8 w-8 text-pp-forest" /></div>
        <h1 className="text-2xl font-extrabold text-pp-text mt-4">Trip complete!</h1>
        <p className="text-sm text-pp-muted mt-1" data-testid="trip-complete-message">
          {savedPos ? `Nice — you saved ${money(trip.saved)} versus your usual store.` : `You spent ${money(Math.abs(trip.difference || 0))} more than planned.`}
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3 text-left">
          {[["Planned total", money(trip.planned_total)], ["Actual (planned items)", money(trip.actual_total)],
            ["Total saved", money(Math.max(0, trip.saved || 0))], ["Difference from estimate", money(trip.difference || 0)],
            ["Extra items bought", money(trip.extras_total || 0)], ["Loyalty earned", money(trip.loyalty_earned)]].map(([k, v]) => (
            <div key={k} className="rounded-2xl bg-pp-cream p-3"><p className="text-xs text-pp-muted">{k}</p><p className="font-bold text-pp-text">{v}</p></div>
          ))}
        </div>
        <Button onClick={() => navigate("/savings")} data-testid="see-savings" className="mt-6 w-full h-12 rounded-full bg-pp-forest font-bold"><PiggyBank className="h-5 w-5 mr-2" /> See my savings</Button>
        <Button variant="ghost" onClick={() => navigate("/trips")} className="mt-2 w-full rounded-full">View all trips</Button>
      </div>
    </div>
  );
}

export default function TripMode() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { prefs } = useAuth();
  const [trip, setTrip] = useState(null);
  const [params] = useSearchParams();
  const [completeOpen, setCompleteOpen] = useState(params.get("checkout") === "1");
  const [actual, setActual] = useState("");
  const [busy, setBusy] = useState(false);
  const [shared, setShared] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const load = useCallback(() => client.get(`/trips/${tripId}`).then((r) => setTrip(r.data)).catch(() => toast.error("Trip not found")), [tripId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  if (!trip) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-pp-forest" /></div>;
  if (trip.status === "completed") return <Completed trip={trip} navigate={navigate} />;

  const home = [prefs?.home_lat || 51.0447, prefs?.home_lng || -114.0719];
  const items = trip.items || [];
  const collected = items.filter((i) => i.checked).length;
  const pct = items.length ? (collected / items.length) * 100 : 0;

  const update = async (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setTrip({ ...trip, items: next });
    await client.patch(`/trips/${tripId}`, { items: next });
  };

  const share = async () => {
    let url;
    try {
      const { data } = await client.post(`/trips/${tripId}/share`);
      url = window.location.origin + data.url;
      setShared(true);
    } catch { toast.error("Could not create live link"); return; }
    if (navigator.share) {
      try { await navigator.share({ title: "Shop with me on Pantry Pilot", url }); return; } catch { /* fall through to dialog */ }
    }
    setShareUrl(url);
  };
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.info("Press and hold the link to copy it"); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/trips/${tripId}/complete`, { actual_total: actual ? +actual : null, duration_minutes: 45 });
      setTrip(data); setCompleteOpen(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const byStore = {};
  items.forEach((it, i) => { (byStore[it.store_name] = byStore[it.store_name] || []).push({ ...it, idx: i }); });
  const storeInfo = Object.fromEntries((trip.stores || []).map((s) => [s.name, s]));

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <FlowSteps current={2} listId={trip.list_id} tripId={trip.id} />
      <div className="sticky top-16 z-30 -mx-4 px-4 py-3 bg-pp-cream/95 backdrop-blur-md">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-extrabold text-pp-text">Trip Mode</h1>
          <div className="flex items-center gap-2">
            <span data-testid="trip-progress-label" className="text-sm font-bold text-pp-forest">{collected} of {items.length}</span>
            <Button size="sm" variant="outline" onClick={share} data-testid="share-live-cart" className="rounded-full bg-white h-8">
              {shared ? <Radio className="h-4 w-4 mr-1 text-pp-forest animate-pulse" /> : <Share2 className="h-4 w-4 mr-1" />} {shared ? "Live" : "Shop together"}
            </Button>
          </div>
        </div>
        <Progress value={pct} className="mt-2 h-2.5" data-testid="trip-progress" />
      </div>

      <OpenInMaps home={home} stores={trip.stores} />

      {Object.entries(byStore).map(([store, list], si) => {
        const info = storeInfo[store];
        return (
          <div key={store} className="bg-white rounded-3xl p-5 pp-shadow" data-testid={`trip-store-${si}`}>
            <div className="flex items-center gap-3 mb-3">
              <span className="grid place-items-center h-8 w-8 rounded-full text-white text-sm font-bold" style={{ background: STORE_COLORS[list[0].store_code] || "#075B3A" }}>{si + 1}</span>
              <div className="flex-1 min-w-0"><p className="font-extrabold text-pp-text truncate">{store}</p>
                {info?.address && <p className="text-xs text-pp-muted truncate">{info.address}</p>}</div>
            </div>
            {info && <div className="mb-3"><OpenInMaps home={home} stores={[info]} compact /></div>}
            <div className="space-y-2">
              {list.map((it) => (
                <div key={it.idx} data-testid={`trip-item-${it.idx}`}
                  className={`flex items-center gap-3 rounded-2xl p-2.5 transition-colors duration-200 ${it.checked ? "bg-[#EBF7EE]" : it.unavailable ? "bg-[#FDF0ED]" : "bg-pp-cream"}`}>
                  <button onClick={() => update(it.idx, { checked: !it.checked, checked_by: null })} data-testid={`trip-check-${it.idx}`}
                    className={`h-8 w-8 shrink-0 grid place-items-center rounded-full border-2 transition-colors ${it.checked ? "bg-pp-forest border-pp-forest text-white" : "border-pp-border bg-white"}`}>
                    {it.checked && <Check className="h-4 w-4" />}
                  </button>
                  <img src={it.image} alt="" className="h-10 w-10 rounded-lg object-contain bg-white" />
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm text-pp-text truncate ${it.checked ? "line-through text-pp-muted" : ""}`}>{it.product_name} × {it.quantity}</p>
                    <p className="text-xs text-pp-muted truncate">{money(it.line_total)}{it.loyalty_program ? ` · ${it.loyalty_program}` : ""}{it.checked_by ? ` · got by ${it.checked_by}` : ""}</p>
                  </div>
                  <button onClick={() => update(it.idx, { unavailable: !it.unavailable })} aria-label="Mark unavailable" data-testid={`trip-unavailable-${it.idx}`} className="text-pp-muted hover:text-pp-error p-1"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <Button data-testid="checkout-button" onClick={() => setCompleteOpen(true)} className="w-full h-12 rounded-full bg-pp-forest text-base font-bold">
        <Flag className="h-5 w-5 mr-2" /> Finish trip & scan receipt
      </Button>

      <Dialog open={!!shareUrl} onOpenChange={(o) => !o && setShareUrl("")}>
        <DialogContent>
          <DialogHeader><DialogTitle>Shop together, live</DialogTitle>
            <DialogDescription>Send this link to your household. Anyone with it can check items off while you shop.</DialogDescription></DialogHeader>
          <Input readOnly value={shareUrl} onFocus={(e) => e.target.select()} className="bg-white" data-testid="share-link-input" />
          <Button onClick={copyShare} data-testid="copy-share-link" className="rounded-full bg-pp-forest">Copy link</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5 text-pp-forest" /> Checkout</DialogTitle>
            <DialogDescription>Snap your receipt so we can read each item. Extra items you grabbed won't skew your savings.</DialogDescription></DialogHeader>
          <ReceiptScanner tripId={tripId} onSaved={(d) => { if (d.trip) { setTrip(d.trip); setCompleteOpen(false); } }} />
          <div className="border-t border-pp-border pt-3 space-y-2">
            <p className="text-xs text-pp-muted">No receipt handy? Planned total was <b className="text-pp-text">{money(trip.planned_total)}</b>.</p>
            <div className="flex gap-2">
              <Input type="number" step="0.01" data-testid="actual-total-input" value={actual} onChange={(e) => setActual(e.target.value)} placeholder={String(trip.planned_total)} className="bg-white h-11" />
              <Button onClick={finish} disabled={busy} data-testid="complete-trip" className="rounded-full bg-pp-forest h-11">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Complete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
